import React, { useEffect, useMemo, useState } from 'react';
import { MinistriesService, type Evento } from '../../services/ministries/ministries.service';

interface CalendarViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const colorPalette = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-red-500',
  'bg-indigo-500',
  'bg-teal-500',
];

interface EventoConColor extends Evento {
  ministerioNombres: string;
  colorIndex: number;
}

export const CalendarView: React.FC<CalendarViewProps> = ({ isOpen, onClose }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventos, setEventos] = useState<EventoConColor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const fetchEventos = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await MinistriesService.findAllEventosPublic();
        const mapped = (data || []).map((ev, idx) => ({
          ...ev,
          ministerioNombres: (ev.ministerios || []).map((m: any) => m.nombre).join(', ') || 'Iglesia',
          colorIndex: idx % colorPalette.length,
        }));
        mapped.sort((a, b) => a.fecha.localeCompare(b.fecha));
        if (!cancelled) setEventos(mapped);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error al cargar eventos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEventos();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  const esEventoPasado = (fecha: string) => fecha.slice(0, 10) < todayStr;

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const eventosPorDia = useMemo(() => {
    const map: Record<string, EventoConColor[]> = {};
    eventos.forEach((ev) => {
      const key = ev.fecha.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [eventos]);

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);

  const calendarDays: Array<{ date: number | null; fullDate: string }> = [];
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  for (let i = 0; i < firstDay; i++) {
    calendarDays.push({ date: null, fullDate: '' });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      date: day,
      fullDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    });
  }

  const formatFecha = (fecha: string) => {
    const [y, m, d] = fecha.slice(0, 10).split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const eventosDelMes = useMemo(() => {
    return eventos
      .filter((ev) => {
        const [y, m] = ev.fecha.slice(0, 10).split('-').map(Number);
        return y === year && m - 1 === month;
      })
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [eventos, year, month]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className={`max-w-6xl w-full max-h-[95vh] h-[95vh] overflow-y-auto rounded-2xl ${
        'bg-white border-gray-200'
      } border shadow-2xl`}>

        {/* Header */}
        <div className={`sticky top-0 p-6 border-b ${
          'border-gray-200 bg-white'
        } rounded-t-2xl z-10`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xs font-medium text-gray-800 mb-1">
                Calendario de Eventos
              </h2>
              <p className="text-gray-600 text-xs">
                Mantente al día con todas nuestras actividades
              </p>
            </div>
            <button
              onClick={onClose}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Calendario */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={previousMonth}
                  className={`p-2 rounded-lg transition-colors ${
                    'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <h3 className="text-xs font-medium text-gray-800">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h3>

                <button
                  onClick={nextMonth}
                  className={`p-2 rounded-lg transition-colors ${
                    'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {loading && (
                <div className="flex items-center justify-center py-12">
                  <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}

              {!loading && error && (
                <div className="text-center text-red-600 py-8">{error}</div>
              )}

              {!loading && !error && (
                <>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
                      <div key={day} className="text-center font-medium text-gray-500 py-2 text-xs">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {calendarDays.map((day, index) => {
                      const dayEvents = day.fullDate ? eventosPorDia[day.fullDate] || [] : [];
                      return (
                        <div key={index} className="h-24">
                          {day.date !== null ? (
                            <div className={`w-full h-full relative rounded-lg flex flex-col items-start justify-start p-1.5 text-xs transition-colors cursor-pointer ${
                              dayEvents.length > 0
                                ? 'bg-gray-50 text-gray-800 border border-gray-200'
                                : 'hover:bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              <span className="font-medium text-xs mb-0.5">{day.date}</span>
                              <div className="w-full flex flex-col gap-0.5 overflow-hidden">
                                {dayEvents.slice(0, 2).map((ev) => {
                                  const pasado = esEventoPasado(ev.fecha);
                                  const bgClass = pasado ? 'bg-gray-400' : colorPalette[ev.colorIndex];
                                  return (
                                    <span
                                      key={ev.id}
                                      className={`${bgClass} text-white text-[10px] px-1 py-0.5 rounded truncate`}
                                      title={ev.titulo}
                                    >
                                      {ev.titulo}
                                    </span>
                                  );
                                })}
                                {dayEvents.length > 2 && (
                                  <span className="text-[10px] text-gray-500 pl-0.5">
                                    +{dayEvents.length - 2}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-full"></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Lista de eventos del mes */}
            <div className="flex flex-col h-full min-h-0">
              <h4 className="text-xs font-medium text-gray-800 mb-4">
                Eventos de {monthNames[currentDate.getMonth()]}
              </h4>

              {loading && (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}

              {!loading && eventosDelMes.length === 0 && (
                <div className="text-center text-gray-500 py-8 text-xs">
                  No hay eventos este mes
                </div>
              )}

              {!loading && eventosDelMes.length > 0 && (
                <div className="space-y-3 overflow-y-auto pr-1 max-h-[560px]">
                  {eventosDelMes.map((ev) => {
                    const pasado = esEventoPasado(ev.fecha);
                    const dotColor = pasado ? 'bg-gray-400' : colorPalette[ev.colorIndex];
                    return (
                      <div
                        key={ev.id}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                          pasado
                            ? 'bg-gray-100 border-gray-200'
                            : 'bg-white border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full ${dotColor} mt-1 shrink-0`}></div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-xs font-medium ${pasado ? 'text-gray-500' : 'text-gray-800'}`}>
                            {ev.titulo}
                          </h5>
                          <p className={`text-xs ${pasado ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatFecha(ev.fecha)} · {ev.horaInicio || '--:--'}
                            {ev.horaFin ? ` - ${ev.horaFin}` : ''}
                          </p>
                          <p className={`text-xs ${pasado ? 'text-gray-400' : 'text-gray-500'}`}>
                            {ev.ministerioNombres}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
